import { pgaComTld } from "../../../utils"
import { ADDITIONAL_REQUIREMENTS } from "./constants/additionalRequirements"
import { REQUIREMENT } from "./constants/levels"
import { CHANGE_FORM_URLS } from "./constants/externalUrls"
import { MEMBER_APPLICATIONS_URL } from "../../../../lib/ptmApplicationsRedirect"

export const CHECKLIST_KEY = {
  EXPERIENCE_CREDITS: "experience_credits",
  EMPLOYMENT: "employment",
  PAT: REQUIREMENT.PLAYING_ABILITY_TEST,
  AGE: "age",
  GOOD_STANDING: "good_standing",
  ACCEPTABLE_PROGRESS: "acceptable_progress",
}

export const JOURNEY_STANDING_SUSPENSION_CODES = ["GPTP", "NPDU", "COEV"]

const DEFAULT_CREDIT_THRESHOLD = 28

const isExplicitlyIncomplete = criterion => criterion?.complete === false
const hasExpiredProgressWindow = criterion => criterion?.expired === true

export const hasJourneyStandingSuspension = (suspensions = []) =>
  suspensions.some(suspension =>
    JOURNEY_STANDING_SUSPENSION_CODES.includes(suspension?.code),
  )

export const membershipDuesUrl = () => `https://${pgaComTld}/member/dues`

const isEligibilityPatIncomplete = eligibility => {
  const pat = eligibility?.playingAbilityTest
  if (!pat) return false
  return !pat.complete || Boolean(pat.expired)
}

export const formatExperienceCreditsDescription = credits => {
  if (!credits) {
    return ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.description
  }

  const total = credits.total ?? 0
  const threshold = credits.threshold ?? DEFAULT_CREDIT_THRESHOLD
  const education = credits.educationCredits
  const employment = credits.employmentCredits
  const hasSplit = education != null && employment != null
  const split = hasSplit
    ? ` (${education} education, ${employment} employment)`
    : ""

  return `You have ${total} of the ${threshold} credits you need${split}.`
}

export const buildMemberApplicationChecklist = ({
  eligibility,
  showPatWarning,
  suspensions = [],
}) => {
  const items = []
  const showPatRow =
    Boolean(showPatWarning) || isEligibilityPatIncomplete(eligibility)

  if (showPatRow) {
    items.push({
      key: CHECKLIST_KEY.PAT,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.PAT.title,
      description: ADDITIONAL_REQUIREMENTS.PAT.description,
      primaryLinkText: ADDITIONAL_REQUIREMENTS.PAT.primaryLinkText,
      primaryLinkKind: "pat-search",
      secondaryLinkText: ADDITIONAL_REQUIREMENTS.PAT.secondaryLinkText,
      secondaryLinkUrl: ADDITIONAL_REQUIREMENTS.PAT.secondaryLinkUrl,
    })
  }

  const credits = eligibility?.experienceCredits
  const showCreditsExplainer = !eligibility
  const showCreditsLeftover = isExplicitlyIncomplete(credits)

  if (showCreditsExplainer || showCreditsLeftover) {
    items.push({
      key: CHECKLIST_KEY.EXPERIENCE_CREDITS,
      complete: false,
      hasWarning: showCreditsLeftover,
      title: ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.title,
      description: showCreditsLeftover
        ? formatExperienceCreditsDescription(credits)
        : ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.description,
      secondaryLinkText: ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.linkText,
      secondaryLinkUrl: ADDITIONAL_REQUIREMENTS.EXPERIENCE_CREDITS.linkUrl,
    })
  }

  if (!eligibility) {
    return items
  }

  if (isExplicitlyIncomplete(eligibility.activelyEmployed)) {
    items.push({
      key: CHECKLIST_KEY.EMPLOYMENT,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.EMPLOYMENT.title,
      description: ADDITIONAL_REQUIREMENTS.EMPLOYMENT.description,
      primaryLinkText: ADDITIONAL_REQUIREMENTS.EMPLOYMENT.primaryLinkText,
      primaryLinkUrl: CHANGE_FORM_URLS.NEW_EMPLOYMENT,
    })
  }

  if (isExplicitlyIncomplete(eligibility.age)) {
    items.push({
      key: CHECKLIST_KEY.AGE,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.AGE.title,
      description: ADDITIONAL_REQUIREMENTS.AGE.description,
    })
  }

  if (
    isExplicitlyIncomplete(eligibility.goodStanding) &&
    !hasJourneyStandingSuspension(suspensions)
  ) {
    items.push({
      key: CHECKLIST_KEY.GOOD_STANDING,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.GOOD_STANDING.title,
      description: ADDITIONAL_REQUIREMENTS.GOOD_STANDING.description,
      primaryLinkText: ADDITIONAL_REQUIREMENTS.GOOD_STANDING.primaryLinkText,
      primaryLinkUrl: membershipDuesUrl(),
    })
  }

  if (hasExpiredProgressWindow(eligibility.acceptableProgress)) {
    items.push({
      key: CHECKLIST_KEY.ACCEPTABLE_PROGRESS,
      complete: false,
      hasWarning: true,
      title: ADDITIONAL_REQUIREMENTS.ACCEPTABLE_PROGRESS.title,
      description: ADDITIONAL_REQUIREMENTS.ACCEPTABLE_PROGRESS.description,
      primaryLinkText:
        ADDITIONAL_REQUIREMENTS.ACCEPTABLE_PROGRESS.primaryLinkText,
      primaryLinkUrl: MEMBER_APPLICATIONS_URL,
    })
  }

  return items
}
